import React from 'react';
import { motion } from 'framer-motion';
import type { SuspensionConnector } from '@/types/suspension-connector';
import { SUSPENSION_CONFIGS } from '@/types/suspension-connector';
import styles from './SuspensionConnectorShape.module.css';

interface SuspensionConnectorShapeProps {
  connector: SuspensionConnector;
  sourcePosition: { x: number; y: number };
  targetPosition: { x: number; y: number };
  isSelected: boolean;
  isPlaying: boolean;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

const CONNECTOR_WIDTH = 48;
const CONNECTOR_HEIGHT = 24;

const SuspensionConnectorShapeComponent: React.FC<SuspensionConnectorShapeProps> = ({
  connector,
  sourcePosition,
  targetPosition,
  isSelected,
  isPlaying,
  onClick,
  onContextMenu,
}) => {
  // Get configuration for this suspension type
  const config = SUSPENSION_CONFIGS[connector.suspensionType];

  // Calculate midpoint between source and target
  const midX = (sourcePosition.x + targetPosition.x) / 2;
  const midY = (sourcePosition.y + targetPosition.y) / 2;

  // Position the connector at the midpoint
  const positionStyle = {
    left: `${midX - CONNECTOR_WIDTH / 2}px`,
    top: `${midY - CONNECTOR_HEIGHT / 2}px`,
  };

  // Calculate angles and distances for connection arcs
  const sourceDistance = Math.sqrt(
    Math.pow(sourcePosition.x - midX, 2) + Math.pow(sourcePosition.y - midY, 2)
  );

  const sourceAngle = Math.atan2(sourcePosition.y - midY, sourcePosition.x - midX);
  const targetAngle = Math.atan2(targetPosition.y - midY, targetPosition.x - midX);

  // Start and end points on the connector for arcs
  const arcStartX = (CONNECTOR_WIDTH / 2) * Math.cos(sourceAngle);
  const arcStartY = (CONNECTOR_HEIGHT / 2) * Math.sin(sourceAngle);
  const arcEndX = (CONNECTOR_WIDTH / 2) * Math.cos(targetAngle);
  const arcEndY = (CONNECTOR_HEIGHT / 2) * Math.sin(targetAngle);

  // Animation variants
  const variants = {
    initial: {
      scale: 0,
      opacity: 0,
    },
    animate: {
      scale: 1,
      opacity: 1,
    },
    selected: {
      boxShadow: [
        `0 0 8px rgba(74, 144, 226, 0.4)`,
        `0 0 12px rgba(74, 144, 226, 0.6)`,
        `0 0 8px rgba(74, 144, 226, 0.4)`,
      ],
    },
    playing: {
      scale: [1, 1.08, 1],
      boxShadow: [
        `0 0 8px rgba(74, 144, 226, 0.4)`,
        `0 0 16px rgba(74, 144, 226, 0.8)`,
        `0 0 8px rgba(74, 144, 226, 0.4)`,
      ],
    },
  };

  return (
    <motion.div
      className={styles.connectorContainer}
      style={positionStyle}
      initial="initial"
      animate="animate"
      transition={{
        duration: 0.3,
        ease: 'backOut',
      }}
      onClick={onClick}
      onContextMenu={onContextMenu}
      role="button"
      aria-label={`Suspension connector: ${config.label}`}
      tabIndex={0}
    >
      {/* SVG for connection arcs */}
      <svg
        className={styles.connectionArcs}
        viewBox={`-${sourceDistance} -${CONNECTOR_HEIGHT} ${sourceDistance * 2} ${CONNECTOR_HEIGHT * 2}`}
        preserveAspectRatio="none"
        width="100%"
        height="100%"
      >
        {/* Arc to source chord (preparation note) */}
        <path
          className={styles.arc}
          d={`M ${arcStartX} ${arcStartY} Q ${arcStartX * 0.5} ${arcStartY * 0.3} 0 0`}
          stroke={config.color}
          strokeWidth="1.5"
          fill="none"
          opacity="0.5"
        />

        {/* Arc to target chord (resolution note) */}
        <path
          className={styles.arc}
          d={`M ${arcEndX} ${arcEndY} Q ${arcEndX * 0.5} ${arcEndY * 0.3} 0 0`}
          stroke={config.color}
          strokeWidth="1.5"
          fill="none"
          opacity="0.5"
        />
      </svg>

      {/* Main connector capsule */}
      <motion.div
        className={styles.capsule}
        style={{
          backgroundColor: config.color,
          borderColor: config.color,
        }}
        variants={variants}
        animate={isPlaying ? 'playing' : isSelected ? 'selected' : 'animate'}
        transition={{
          duration: isPlaying ? 0.8 : isSelected ? 1.5 : 0.3,
          ease: isPlaying ? 'easeInOut' : 'easeOut',
          repeat: isPlaying ? Infinity : isSelected ? Infinity : 0,
        }}
        whileHover={{
          scale: 1.1,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15)`,
        }}
      >
        {/* Label text */}
        <span className={styles.label}>{config.label}</span>
      </motion.div>

      {/* Selection indicator ring */}
      {isSelected && (
        <motion.div
          className={styles.selectionRing}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        />
      )}
    </motion.div>
  );
};

SuspensionConnectorShapeComponent.displayName = 'SuspensionConnectorShape';

export const SuspensionConnectorShape = React.memo(SuspensionConnectorShapeComponent);
